import { Component, effect, ElementRef, HostListener, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSearch, faFile, faCalendar, faXmark } from '@fortawesome/pro-light-svg-icons';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { DocumentHit, PlanHit, TypesenseService } from '../../services/typesense.service';

interface SearchResultItem {
  type: 'document' | 'plan';
  data: DocumentHit | PlanHit;
}

@Component({
  selector: 'app-search-modal',
  standalone: true,
  imports: [
    FormsModule,
    FontAwesomeModule,
    RouterModule
  ],
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss']
})
export class SearchModalComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('resultsContainer') resultsContainer!: ElementRef<HTMLDivElement>;

  // Persisted search query using ngxtension
  private persistedQuery = injectLocalStorage<string>('search_modal_query', {
    defaultValue: '',
  });

  searchQuery = this.persistedQuery() || '';
  documentResults: DocumentHit[] = [];
  planResults: PlanHit[] = [];
  selectedIndex = -1;
  hasSearched = false;
  isSearching = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Icons
  searchIcon = faSearch;
  documentIcon = faFile;
  planIcon = faCalendar;
  closeIcon = faXmark;

  activeModal = inject(NgbActiveModal);
  private typesenseService = inject(TypesenseService);
  private router = inject(Router);

  constructor() {
    // Initialize search with debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.performSearch(query);
      // Persist the query
      this.persistedQuery.set(query);
    });

    // Restore last search query if exists
    if (this.searchQuery) {
      this.performSearch(this.searchQuery);
    }

    // Focus input after view init
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
      // Select all text so user can easily type new query
      this.searchInput?.nativeElement?.select();
    }, 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.activeModal.dismiss();
      return;
    }

    const totalResults = this.getTotalResultCount();
    if (totalResults === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, totalResults - 1);
        this.scrollSelectedIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.scrollSelectedIntoView();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0) {
          const item = this.getItemAtIndex(this.selectedIndex);
          if (item) {
            this.selectResult(item);
          }
        }
        break;
    }
  }

  private scrollSelectedIntoView() {
    if (this.selectedIndex < 0) return;

    setTimeout(() => {
      const container = this.resultsContainer?.nativeElement;
      if (!container) return;

      const selectedElement = container.querySelector('.search-result-item.selected') as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  onSearch() {
    if (this.searchQuery.trim()) {
      this.searchSubject.next(this.searchQuery.trim());
      this.selectedIndex = -1;
    } else {
      this.clearResults();
    }
  }

  private performSearch(query: string) {
    if (!query) {
      this.clearResults();
      return;
    }

    this.hasSearched = true;
    this.isSearching = true;

    this.typesenseService.search(query).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (results) => {
        this.documentResults = results.documents;
        this.planResults = results.plans;
        this.selectedIndex = -1;
        this.isSearching = false;
      },
      error: (err) => {
        console.error('Search error:', err);
        this.documentResults = [];
        this.planResults = [];
        this.isSearching = false;
      }
    });
  }

  selectResult(item: SearchResultItem) {
    if (item.type === 'document') {
      const doc = item.data as DocumentHit;
      this.router.navigate(['/document', doc.id]);
    } else {
      const plan = item.data as PlanHit;
      this.router.navigate(['/plan', plan.plan_id]);
    }
    this.activeModal.close();
  }

  selectDocumentResult(doc: DocumentHit) {
    this.selectResult({ type: 'document', data: doc });
  }

  selectPlanResult(plan: PlanHit) {
    this.selectResult({ type: 'plan', data: plan });
  }

  private clearResults() {
    this.documentResults = [];
    this.planResults = [];
    this.selectedIndex = -1;
    this.hasSearched = false;
  }

  getTotalResultCount(): number {
    return this.documentResults.length + this.planResults.length;
  }

  getItemAtIndex(index: number): SearchResultItem | null {
    if (index < this.documentResults.length) {
      return { type: 'document', data: this.documentResults[index] };
    }
    const planIndex = index - this.documentResults.length;
    if (planIndex < this.planResults.length) {
      return { type: 'plan', data: this.planResults[planIndex] };
    }
    return null;
  }

  isSelected(type: 'document' | 'plan', index: number): boolean {
    if (type === 'document') {
      return this.selectedIndex === index;
    } else {
      return this.selectedIndex === this.documentResults.length + index;
    }
  }

  hasResults(): boolean {
    return this.documentResults.length > 0 || this.planResults.length > 0;
  }

  truncateContent(content: string, maxLength: number = 50): string {
    if (!content) return '';
    const stripped = content.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  }
}
